import * as React from "react";
import styled from "styled-components";
import { ReactSVG } from "react-svg";
import { observer } from "mobx-react-lite";
import { Box } from "src/components/Box";
import { Button } from "src/components/Button";
import { Text } from "src/components/Text";
import { LanguageSwitcher } from "src/components/LanguageSwitcher";
import { HERA_BASE_URL, MediaQueries, SDK_BASE_URL } from "src/constants";
import { PassportAssets } from "src/assets";
import { useTranslation } from "src/hooks/useTranslation";
import { useRootStore } from "src/stores/Root.store";
import { isSdkFlow } from "src/helpers/flow";
import translations from "src/modules/translations.json";

const BannerEl = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(p) => p.theme.layout.margin.m}px
    ${(p) => p.theme.layout.margin.l}px;
`;

const WrapperEl = styled(Box)`
  min-height: calc(${(p) => p.theme.viewHeight} - 80px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${(p) => p.theme.layout.margin.l}px;
`;

const ContentEl = styled(Box)`
  max-width: 520px;
  width: 100%;
`;

const TitleEl = styled(Text)`
  font-size: ${(p) => p.theme.text.size.large};
  margin-bottom: ${(p) => p.theme.layout.margin.m}px;

  ${MediaQueries.screen.medium.minWidth} {
    font-size: ${(p) => p.theme.text.size.xlarge};
  }
`;

const BodyEl = styled.div`
  text-align: left;
  font-size: ${(p) => p.theme.text.size.medium};
  line-height: 22px;
  margin-bottom: ${(p) => p.theme.layout.margin.l}px;
`;

const ParagraphEl = styled.p`
  margin: 0 0 ${(p) => p.theme.layout.margin.m}px;

  strong {
    font-weight: 600;
  }

  a {
    color: inherit;
    text-decoration: underline;
  }
`;

const ListEl = styled.ul`
  margin: 0 0 ${(p) => p.theme.layout.margin.m}px;
  padding-left: ${(p) => p.theme.layout.margin.l}px;

  li {
    margin-bottom: ${(p) => p.theme.layout.margin.xs}px;
    list-style: initial;
  }
`;

const CtaWrapperEl = styled(Box)`
  display: flex;
  justify-content: center;
`;

const EMAIL_RE = /([\w.+-]+@[\w-]+\.[\w.-]+)/g;
const BOLD_RE = /(\*\*[^*]+\*\*)/g;

const renderInline = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  text.split(BOLD_RE).forEach((chunk, i) => {
    if (chunk.startsWith("**") && chunk.endsWith("**")) {
      nodes.push(<strong key={`b-${i}`}>{chunk.slice(2, -2)}</strong>);
      return;
    }
    chunk.split(EMAIL_RE).forEach((seg, j) => {
      if (EMAIL_RE.test(seg)) {
        EMAIL_RE.lastIndex = 0;
        nodes.push(
          <a key={`a-${i}-${j}`} href={`mailto:${seg}`}>
            {seg}
          </a>
        );
      } else if (seg) {
        nodes.push(<React.Fragment key={`t-${i}-${j}`}>{seg}</React.Fragment>);
      }
    });
  });
  return nodes;
};

export const PassportMaintenanceScreen = observer(() => {
  const translate = useTranslation();
  const {
    $translationStore: { language$ },
  } = useRootStore();

  const dashboardUrl = isSdkFlow() ? SDK_BASE_URL : HERA_BASE_URL;

  const langCopy =
    (translations as any)?.[language$]?.maintenance ??
    (translations as any)?.en?.maintenance ??
    {};

  const message1: string = langCopy.message_1 ?? "";
  const affected: string[] = Array.isArray(langCopy.affected)
    ? langCopy.affected
    : [];
  const period: string = langCopy.period ?? "";
  const message2: string = langCopy.message_2 ?? "";
  const closing: string = langCopy.closing ?? "";

  const handleBack = () => {
    if (dashboardUrl) {
      window.location.assign(dashboardUrl);
    }
  };

  return (
    <>
      <BannerEl>
        <ReactSVG src={PassportAssets.midtransLogo} />
        <LanguageSwitcher defaultLanguage={language$} />
      </BannerEl>
      <WrapperEl>
        <ContentEl>
          <TitleEl fontWeight="demi">{translate("maintenance.title")}</TitleEl>
          <BodyEl>
            {message1 && <ParagraphEl>{renderInline(message1)}</ParagraphEl>}
            {affected.length > 0 && (
              <ListEl>
                {affected.map((item, idx) => (
                  <li key={idx}>{renderInline(item)}</li>
                ))}
              </ListEl>
            )}
            {period && <ParagraphEl>{renderInline(period)}</ParagraphEl>}
            {message2 && <ParagraphEl>{renderInline(message2)}</ParagraphEl>}
            {closing && <ParagraphEl>{renderInline(closing)}</ParagraphEl>}
          </BodyEl>
          <CtaWrapperEl>
            <Button onClick={handleBack} disabled={!dashboardUrl}>
              {translate("maintenance.cta")}
            </Button>
          </CtaWrapperEl>
        </ContentEl>
      </WrapperEl>
    </>
  );
});
